import { Fragment, useState, ChangeEvent } from 'react';
import ApiRequest from 'Shared/api_2_0';
import { Box, Text, Link, Heading, Flex, Select, Button } from '@chakra-ui/react';
import { _js } from '@ifixit/localize';

type NotificationType = {
   read: boolean;
   signature: string;
   metadata: {
      icon: string;
      url: string;
      message: string;
      relative_time: any;
   };
};

type NotificationData = {
   notif_count: number;
   recent_notifications: NotificationType[];
   unread_notifications: NotificationType[];
   own_profile: boolean;
};

export function Notifications({
   data,
   setUnreadNotifCount,
}: {
   data: NotificationData;
   setUnreadNotifCount: (_count: number) => void;
}) {
   // The notification state changes only when we need to make an API call
   // to refresh notification metadata (i.e. marking a notification as read)
   const [recentNotifications, setRecentNotifications] = useState(data.recent_notifications);
   const [unreadNotifications, setUnreadNotifications] = useState(data.unread_notifications);

   // The filter state changes when we toggle between All/Unread notifs.
   // Client already has all necessary data so no need for an API call,
   // we just use conditional rendering.
   const [filter, setFilter] = useState('all');

   const visibleNotifications: NotificationType[] =
      filter === 'all' ? recentNotifications : unreadNotifications;

   return (
      <Flex direction="column">
         <Heading
            as="h2"
            m="0 0 8px"
            fontSize="22px"
            fontWeight="semibold"
            borderBottomWidth="4px"
            borderColor="gray.100"
         >
            {_js('Recent Notifications')}
         </Heading>
         <NotificationOptions
            recentNotifications={recentNotifications}
            setRecentNotifications={setRecentNotifications}
            unreadNotifications={unreadNotifications}
            setUnreadNotifications={setUnreadNotifications}
            setUnreadNotifCount={setUnreadNotifCount}
            filter={filter}
            setFilter={setFilter}
            ownProfile={data.own_profile}
         />
         {visibleNotifications.length > 0 ? (
            <NotificationList
               notifs={visibleNotifications}
               recentNotifications={recentNotifications}
               unreadNotifications={unreadNotifications}
               ownProfile={data.own_profile}
               setRecentNotifications={setRecentNotifications}
               setUnreadNotifications={setUnreadNotifications}
            />
         ) : (
            <NoNotifications />
         )}
      </Flex>
   );
}

const NotificationOptions = ({
   recentNotifications,
   setRecentNotifications,
   unreadNotifications,
   setUnreadNotifications,
   filter,
   setFilter,
   setUnreadNotifCount,
   ownProfile,
}: {
   recentNotifications: NotificationType[];
   setRecentNotifications: (_notifications: NotificationType[]) => void;
   unreadNotifications: NotificationType[];
   setUnreadNotifications: (_notifications: NotificationType[]) => void;
   filter: string;
   setFilter: (_filter: string) => void;
   setUnreadNotifCount: (_count: number) => void;
   ownProfile: boolean;
}) => {
   const hasUnreadNotifications = unreadNotifications.length > 0;
   const showMarkAllReadButton = hasUnreadNotifications && ownProfile;
   return (
      <Flex justify={hasUnreadNotifications ? 'space-between' : 'flex-start'} mb="16px">
         <FilterSelect filter={filter} setFilter={setFilter} />
         {showMarkAllReadButton ? (
            <MarkAllReadButton
               recentNotifications={recentNotifications}
               setRecentNotifications={setRecentNotifications}
               setUnreadNotifications={setUnreadNotifications}
               setUnreadNotifCount={setUnreadNotifCount}
            />
         ) : null}
      </Flex>
   );
};

const MarkAllReadButton = ({
   recentNotifications,
   setRecentNotifications,
   setUnreadNotifications,
   setUnreadNotifCount,
}: {
   recentNotifications: NotificationType[];
   setRecentNotifications: (_notifications: NotificationType[]) => void;
   setUnreadNotifications: (_notifications: NotificationType[]) => void;
   setUnreadNotifCount: (_count: number) => void;
}) => {
   function markAllRead() {
      new ApiRequest('notifications/all', {
         method: 'DELETE',
      }).send();
      setRecentNotifications(
         recentNotifications.map((notif: NotificationType) => ({ ...notif, read: true }))
      );
      setUnreadNotifications([]);
      setUnreadNotifCount(0);
   }

   return (
      <Button
         variant="solid"
         bg="transparent"
         color="gray.500"
         leftIcon={<i className="fa fa-times" />}
         onClick={markAllRead}
         mr="-16px"
      >
         {_js('Mark all as read')}
      </Button>
   );
};

const FilterSelect = ({
   filter,
   setFilter,
}: {
   filter: string;
   setFilter: (_filter: string) => void;
}) => (
   <Flex>
      <Text mr="10px">{_js('Show:')}</Text>
      <Select
         value={filter}
         variant="filled"
         id="filter-dropdown"
         onChange={(event: ChangeEvent<HTMLSelectElement>) => setFilter(event.target.value)}
      >
         <option value="all">{_js('All')}</option>
         <option value="unread">{_js('Unread')}</option>
      </Select>
   </Flex>
);

const NoNotifications = () => (
   <Flex direction="column" align="center" sx={{ '.fa': { fontSize: '50px' } }}>
      <i className="fa fa-smile-o icon" />
      <Text>{_js("You're on top of things! No new notifications.")}</Text>
   </Flex>
);

const NotificationList = ({
   notifs,
   recentNotifications,
   unreadNotifications,
   ownProfile,
   setRecentNotifications,
   setUnreadNotifications,
}: {
   notifs: NotificationType[];
   recentNotifications: NotificationType[];
   unreadNotifications: NotificationType[];
   ownProfile: boolean;
   setRecentNotifications: (_notifs: NotificationType[]) => void;
   setUnreadNotifications: (_notifs: NotificationType[]) => void;
}) => {
   const markAsRead = (readNotif: NotificationType) => {
      const readSignature = readNotif.signature;
      new ApiRequest(`notifications/?ids=${readSignature}`).send();

      // Update recent notificationsr
      const updatedRecentNotifications = recentNotifications.map(
         (notification: NotificationType) =>
            notification.signature === readSignature
               ? { ...notification, read: true }
               : notification
      );
      setRecentNotifications(updatedRecentNotifications);

      // Update unread notifications
      const updatedUnreadNotifications = unreadNotifications.filter(
         (notification: NotificationType) => notification.signature !== readSignature
      );
      setUnreadNotifications(updatedUnreadNotifications);
   };

   return (
      <Fragment>
         {notifs.map((notification: NotificationType, index: number) => (
            <NotificationListItem
               key={index}
               notification={notification}
               ownProfile={ownProfile}
               markAsRead={markAsRead}
            />
         ))}
      </Fragment>
   );
};

const NotificationListItem = ({
   notification,
   ownProfile,
   markAsRead,
}: {
   notification: NotificationType;
   ownProfile: boolean;
   markAsRead: (_notif: NotificationType) => void;
}) => {
   const iconColor = notification.read ? 'gray.300' : 'brand.500';

   return (
      <Flex
         fontSize="14px"
         justify="space-between"
         py="10px"
         sx={{ '.fa': { fontSize: '18px', color: iconColor } }}
      >
         <Flex>
            <i className={`fa fa-${notification.metadata.icon}`} />
            <Link
               color="brand.500"
               ml="10px"
               fontWeight={notification.read ? 'normal' : 'semibold'}
               onClick={() => {
                  if (!notification.read && ownProfile) {
                     markAsRead(notification);
                  }
                  window.open(notification.metadata.url, '_blank');
               }}
            >
               {notification.metadata.message}
            </Link>
         </Flex>
         <Flex align="center">
            <Box
               ml="10px"
               color="gray.400"
               whiteSpace="nowrap"
               fontStyle="italic"
               dangerouslySetInnerHTML={{ __html: notification.metadata.relative_time }}
            />
            {!notification.read && ownProfile ? (
               <Box
                  _hover={{ textDecoration: 'none' }}
                  cursor="pointer"
                  ml={3}
                  className="fa fa-times"
                  onClick={() => markAsRead(notification)}
               />
            ) : null}
         </Flex>
      </Flex>
   );
};
